/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { ExclamationCircleFilled, MedicineBoxFilled } from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Image, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  resolveAssetUrl,
  type ImageLike,
} from '@sportninja/common/utils/assetUrl'
import { paragraphSmall } from 'src/components/css'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import type { AttendancePlayer } from '../../types'
import { getPlayerDisplayName } from '../../utils/attendanceRowState'
import { getAttendanceStatusTooltip } from '../../utils/statusCopy'
import { useAttendanceSection } from './AttendanceSectionContext'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

/** NAME column: avatar + name + injury / suspension tags. */
const NameCell = ({ data }: ICellRendererParams<AttendancePlayer>) => {
  // Read-only rows carry no tooltips (the column headers keep theirs).
  const { isReadOnly } = useAttendanceSection()

  if (!data) {
    return null
  }

  return (
    <div css={wrapStyle}>
      <div css={avatarWrapperStyle}>
        <Image
          src={resolveAssetUrl(data.image as ImageLike) ?? PLAYER_PLACEHOLDER}
          alt={data.full_name ?? ''}
          preview={false}
          css={avatarImageStyle}
          fallback={PLAYER_PLACEHOLDER}
        />
      </div>
      <span css={nameStyle}>
        {getPlayerDisplayName(data)}
        {data.is_injured && (
          <Tooltip
            title={
              isReadOnly ? undefined : getAttendanceStatusTooltip('injury')
            }
            zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}
          >
            <MedicineBoxFilled
              css={tagIconStyle}
              aria-label={getAttendanceStatusTooltip('injury')}
            />
          </Tooltip>
        )}
        {data.is_suspended && (
          <Tooltip
            title={
              isReadOnly ? undefined : getAttendanceStatusTooltip('suspended')
            }
            zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}
          >
            <ExclamationCircleFilled
              css={tagIconStyle}
              aria-label={getAttendanceStatusTooltip('suspended')}
            />
          </Tooltip>
        )}
      </span>
    </div>
  )
}

export default NameCell

const wrapStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`
const avatarWrapperStyle = css`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  background-color: ${colors.NEUTRAL_0};
  line-height: 0;
`
const avatarImageStyle = css`
  width: 32px;
  height: 32px;
  object-fit: cover;
`
const nameStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_0};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
const tagIconStyle = css`
  margin-left: 6px;
  color: ${colors.ERROR_300};
`
